import React, { useState, useRef, createContext } from 'react';
import PropTypes from 'prop-types';

// sushi
import grey from 'sushiweb/tokens/color/grey';

// components
import Card from '../../../../../../common/components/Card';
import StepHeader from '../../../../../../common/components/StepHeader';

// creating context for all children to consume
export const FormCacheContext = createContext({});

function FormTemplate({ children, toStep }) {
  const [currentStep, setCurrentStep] = useState(toStep);
  const [showStepHeader, setShowStepHeader] = useState(false);

  const stepsVisibility = show => {
    if (show) setShowStepHeader(true);
    else setShowStepHeader(false);
  };

  const formDataCache = useRef({});

  const updateCachedFormData = newData => {
    formDataCache.current = {
      ...formDataCache.current,
      ...newData,
    };
  };

  // use getter only
  const getCachedFormData = () => formDataCache.current;

  const lastStep = (() => {
    if (children) {
      if ('length' in children) return children.length;
      return 1;
    }

    return 0;
  })();

  const goToNextStep = () => {
    setCurrentStep(current => (current <= lastStep ? current + 1 : current));
  };

  const goToPrevStep = () => {
    setCurrentStep(current => (current > 1 ? current - 1 : current));
  };

  // To jump to a specific step
  const goToStep = step => {
    setCurrentStep(step);
  };

  const getStepsTitles = () => {
    const titles = {};
    if (children) {
      // for more than one child
      if (React.Children.count(children) > 1) {
        children.forEach(child => {
          if (child.props.displayName && child.props.step)
            titles[child.props.step] = child.props.displayName;
        });
      }
      // for one child
      else if (children.props.displayName && children.props.step)
        titles[children.props.step] = children.props.displayName;
    }

    return titles;
  };

  // renderStep : renders the step component depending upon the
  // value of currentStep
  // step being rendered depends on its step prop
  const renderStep = step => {
    if (children) {
      if (React.Children.count(children) > 1) {
        let indexOfChild = null;
        children.forEach((child, index) => {
          if ('step' in child.props) {
            // if prop is equal to asked step
            if (child.props.step === step) indexOfChild = index;
          }
        });

        // asked step found
        if (indexOfChild != null) {
          return children[indexOfChild];
        }

        // asked step not found
        return <div>This Step does not exist!!!</div>;
      }

      // for only one child
      if ('step' in children.props) return children;
      // if it does not have any step mentioned do not render it
      // as it will cause unexpected behaviour
      return <div>This Step does not exist!!!</div>;
    }

    // when there is no children to render
    return <div>No Form to show!!!</div>;
  };

  const style = {
    scroll: true,
    hideScroll: true,
    bodyPx: '2rem',
    bodyPy: '1rem',
    footerPy: '1rem',
    footerPx: '2rem',
    justifyHeader: 'space-evenly',
    justifyFooter: 'space-between',
    headerShadow: false,
    footerShadow: false,
  };

  const cardStyle = {
    width: '80vw',
    maxWidth: '80rem',
    maxHeight: '80vh',
    minHeight: '45rem',
  };

  const headerStyle = {
    borderTop: `1px solid ${grey.z200}`,
  };

  const footerStyle = {
    borderTop: `1px solid ${grey.z200}`,
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    padding: '20px',
  };

  const bodyStyle = {
    display: 'flex',
    flexDirection: 'column',
    borderTop: showStepHeader ? 'none' : `1px solid ${grey.z200}`,
    padding: '1rem 3rem',
    margin: '1rem 0rem',
  };

  const greyBodyStyle = {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${grey.z200}`,
    paddingTop: '1rem',
    background: ` ${grey.z50}`,
    borderRadius: '0.6rem',
    marginTop: '10px',
  };
  return (
    <Card {...style} style={cardStyle}>
      {showStepHeader && (
        <Card.Header style={headerStyle}>
          <StepHeader currentStep={currentStep} steps={getStepsTitles()} />
        </Card.Header>
      )}
      <FormCacheContext.Provider
        value={{
          formDataCache,
          updateCachedFormData,
          getCachedFormData,
          bodyStyle,
          greyBodyStyle,
          footerStyle,
          goToPrevStep,
          goToNextStep,
          goToStep,
          currentStep,
          stepsVisibility,
        }}
      >
        {renderStep(currentStep)}
      </FormCacheContext.Provider>
    </Card>
  );
}

FormTemplate.propTypes = {
  children: PropTypes.node.isRequired,
  toStep: PropTypes.number,
};

FormTemplate.defaultProps = {
  toStep: 1,
};

export default FormTemplate;
